"use client";
import React, { useState, useEffect, useMemo } from "react";
import CalcInputValues from "../ui/CalcInputValues";
import Image from "next/image";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";
import { calculateGratuityValue, calculateLumpsumValue } from "@/utils/allCalculators";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { apiGet, apiPost } from "@/utils/apiClient";
import { useLoginModal } from "@/utils/useLoginModal";
import { usePathname, useRouter } from "next/navigation";
import { increaseCart } from "@/redux/features/AddToCart/cartSlice";
import { useDispatch } from "react-redux";
import InformationButton from "@/components/ui/InformationButton";
import { DASHBOARD_ROOT, ROOT_URL } from "@/utils/gloableVariables";

type Fund = {
  id: number;
  amc_code: string;
  scheme_name: string;
  scheme_code: string;
  one_year_abs: number;
  category_id: string;
  rank: string;
};

type FundOption = {
  label: "Large Cap" | "Mid Cap" | "Small Cap" | "Unknown";
  rate: string;
  fund: Fund;
};

type APIResponse = {
  status: boolean;
  message: string;
  cart_id: number;
};

export default function GratuityCalc() {
  const [salary, setSalary] = useState(30000);
  const [dearnessAmount, setDearnessAmount] = useState(5000);
  const [dearnessType, setDearnessType] = useState("Monthly");
  const [years, setYears] = useState(5);
  const [coverageType, setCoverageType] = useState("Covered Under Act");

  const [isOpen, setIsOpen] = useState(false);

  const isTab = useIsMobile(1025);

  const coverageOption = ["Covered Under Act", "Not Covered"];

  const totalPayable = calculateGratuityValue(
    salary,
    dearnessAmount,
    dearnessType === "Annually",
    years,
    coverageType === "Covered Under Act",
  );

  return (
    <>
      <section className="relative text-white text-center mb-12 sm:mb-100 section">
        {/* Floating Calculator */}
        <div className="flex flex-col lg:flex-row gap-6 items-center w-full bg-white text-black rounded-xl shadow-lg p-4 pb-6 xl:p-6">
          {/* Inputs */}
          <div className="w-full lg:w-[40%] space-y-6 xs:space-y-8">
            <CalcInputValues
              label="Basic Salary"
              min={10000}
              max={10000000}
              unit="₹"
              amountValue={salary}
              showFloatingValue
              unitPosition="left"
              onChange={setSalary}
              keyWord={"basic_salary"}
            />

            <CalcInputValues
              label="Dearness Allowance"
              min={100}
              max={1000000}
              unit="₹"
              amountValue={dearnessAmount}
              showFloatingValue
              unitPosition="left"
              modeOptions={["Monthly", "Annually"]}
              defaultMode="Monthly"
              onChange={setDearnessAmount}
              onModeChange={(mode) => setDearnessType(mode)}
              keyWord={"dearness_allowance"}
            />

            <CalcInputValues
              label="Years of Service"
              min={1}
              max={60}
              unit="Years"
              amountValue={years}
              onChange={setYears}
              keyWord={"services_years"}
            />

            <div className="space-y-4.5">
              <div className="flex items-center gap-1">
                <p className="font-semibold text-sm text-left sm:text-base lg:text-lg text-[#222222]">
                  Company Coverage
                </p>
                <InformationButton keywords="company_coverage" className="w-4! h-4! sm:w-5! sm:h-5!" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                {coverageOption.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setCoverageType(opt)}
                    className={`px-4 py-1.5 md:py-2 text-sm md:text-base md:leading-6 leading-4 w-full max-w-fit text-center rounded-full border font-medium transition duration-350 ${coverageType === opt ? "bg-primaryGreen text-white border-primaryGreen" : "border-gray-400 text-gray-700 hover:bg-[#f4fffe] hover:border-primaryGreen"}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {isTab && (
              <button
                onClick={() => setIsOpen(true)}
                className="lg:hidden w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5"
              >
                Calculate
              </button>
            )}
          </div>

          {!isTab && <RightPart className="hidden lg:block" totalPayable={totalPayable} />}
        </div>
      </section>

      {isTab && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          maxSize="max-w-[80%]"
          useDragToClose
          isBlurOverlay
          className="bg-white rounded-b-none sm:rounded-4xl"
        >
          <RightPart className="block lg:hidden" totalPayable={totalPayable} />
        </CustomModal>
      )}
    </>
  );
}

function RightPart({ className, totalPayable }: { className?: string; totalPayable: number }) {
  const [fundOptions, setFundOptions] = useState<FundOption[]>([]);
  const [selectedFund, setSelectedFund] = useState<FundOption | null>(null);
  const [loading, setLoading] = useState(true);

  const categoryMap: Record<string, FundOption["label"]> = {
    "31": "Small Cap",
    "25": "Mid Cap",
    "24": "Large Cap",
  };

  const payable = formatIndianNumber(totalPayable);
  const pathName = usePathname();
  const route = useRouter();
  const dispatch = useDispatch();

  const funds = async () => {
    setLoading(true);
    try {
      const data: Fund[] = await apiGet("/api/gratuityTopFunds", { base: "none" });

      const mapped: FundOption[] = data.map((item) => ({
        label: categoryMap[item.category_id] || "Unknown",
        rate: `${item.one_year_abs}% p.a.`,
        fund: item,
      }));

      setFundOptions(mapped);

      if (mapped.length > 0) {
        setSelectedFund(mapped[0]);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error(error, "error fetching funds");
    }
  };

  useEffect(() => {
    funds();
  }, []);

  const annualReturn = selectedFund?.fund.one_year_abs ?? 0;

  const tenYearSipValue = useMemo(
    () => calculateLumpsumValue(totalPayable, annualReturn, 10),
    [totalPayable, annualReturn],
  );

  const fifteenYearSipValue = useMemo(
    () => calculateLumpsumValue(totalPayable, annualReturn, 15),
    [totalPayable, annualReturn],
  );

  const calcGrowthPercent = (finalValue: number, invested: number): number => {
    if (invested <= 0) return 0;
    return ((finalValue - invested) / invested) * 100;
  };

  const tenYearGrowth = calcGrowthPercent(tenYearSipValue, totalPayable);
  const fifteenYearGrowth = calcGrowthPercent(fifteenYearSipValue, totalPayable);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const handleLogin = () => {
    open();
    setModalKey("gratuity-calculator");
  };

  const handleInvesting = async () => {
    const payload = {
      scheme_code: selectedFund?.fund.scheme_code || "",
      inv_type: "lumpsum",
      amount: payable.toString(),
      sip_date: "",
      offset: "",
      addnl_investment: "",
      folio_no: "",
      source: pathName,
      medium: "Web",
      folio: "",
    };

    if (!isLoggedIn) {
      handleLogin();
    } else {
      const res: APIResponse = await apiPost("addToCart", payload);
      if (res.status) {
        dispatch(increaseCart());
        route.push(`${DASHBOARD_ROOT}single-buy/${res.cart_id}`);
      }
    }
  };

  return (
    <div
      className={`w-full lg:w-[58%] xl:border-2 border-[#A7A7A733] rounded-6xl p-4 pt-0 sm:pt-4 lg:p-0 xl:p-5 bg-white ${className}`}
    >
      <div className="flex items-center justify-between w-full bg-[radial-gradient(50%_307%_at_50%_50%,rgba(0,107,102,0.88)_0%,rgba(0,96,107,0.91)_100%)] py-3.5 sm:py-6 px-4 sm:px-7 rounded-4xl">
        <p className="font-semibold text-2xs sm:text-base text-white">Total Gratuity Payable</p>
        <p className="font-bold text-xl sm:text-2xl md:text-3xl text-[#F3FF47]">₹ {payable}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mt-5">
        {/* Left Box */}
        <div className="w-full md:w-[42%] bg-[#F5F5F5] rounded-4xl p-4 text-left xs:text-center md:text-left">
          <p className="text-primaryGreen font-bold text-base sm:text-[17px] xs:text-xl">
            Better Returns Than FD - Maximize Your Gratuity!
          </p>
          <p className="text-primaryGreen text-2xs sm:text-sm mt-2">Invest your Gratuity as Lumpsum and watch it grow:</p>

          <div className="flex flex-row gap-2 xs:gap-3 md:block mt-4 md:space-y-2.5">
            {loading
              ? [1, 2, 3].map((ind) => <div key={ind} className="h-11 w-full bg-gray-200 rounded-md" />)
              : fundOptions.map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => setSelectedFund(opt)}
                    className={`flex flex-col md:flex-row w-full gap-1 xs:gap-1.5 justify-between items-center rounded-xl py-3 px-0 xs:p-3 border bg-white transition duration-300 ${selectedFund?.label === opt.label ? "border-primaryGreen shadow-[0px_3px_18.3px_0px_#00000024]" : "border-[#E6E6E6] hover:border-primaryGreen"}`}
                  >
                    <span className="text-[#444444] text-xs sm:text-2xs font-medium">{opt.label}</span>
                    <span
                      className={`font-semibold text-2xs sm:text-sm transition duration-300 ${selectedFund?.label === opt.label ? "text-primaryGreen" : "text-primaryBlack"}`}
                    >
                      {opt.rate}
                    </span>
                  </button>
                ))}
          </div>
        </div>

        {/* Right Box */}
        <div className="w-full md:w-[56%] flex flex-col bg-white rounded-4xl p-4 border border-primaryGreen text-left">
          {loading ? (
            <LoadingSatate />
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Image
                  src={`${ROOT_URL}img/AMCsLogo/${selectedFund?.fund.amc_code}.png`}
                  alt={selectedFund?.fund.scheme_name || "Fund Icon"}
                  width={32}
                  height={32}
                  className="w-8 h-8 border border-[#1A51AA66] rounded-full object-contain shrink-0"
                />

                <p className="font-semibold text-2xs text-primaryBlack line-clamp-2">
                  {selectedFund?.fund.scheme_name}
                </p>

                {/* Hide the Fund Name If not Logged In  */}
                {/* {isLoggedIn ? (
                  <>
                    <Image src={`${ROOT_URL}img/AMCsLogo/${selectedFund?.fund.amc_code}.png`} alt={selectedFund?.fund.scheme_name || "Fund Icon"} width={32} height={32} className="w-8 h-8 border border-[#1A51AA66] rounded-full object-contain shrink-0" />

                    <p className="font-semibold text-2xs text-primaryBlack line-clamp-2">{selectedFund?.fund.scheme_name}</p>
                  </>
                ) : (
                  <>
                    <Image src="/next_assets/images/scheme-details/not-suitable-fund.svg" alt={selectedFund?.fund.scheme_name || "Fund Icon"} width={32} height={32} className="w-8 h-8 blur-sm border border-[#1A51AA66] rounded-full overflow-hidden object-contain shrink-0" />

                    <p className="font-semibold text-2xs text-primaryBlack line-clamp-2 blur-sm">This is a dummy fund - login to see the real.</p>
                  </>
                )} */}

                <div className="text-[#1CBA00] text-base leading-5 font-semibold shrink-0 ml-auto">
                  {selectedFund?.fund.one_year_abs}%{" "}
                  <span className="block text-[#B3B3B3] text-2xs text-center font-medium">p.a.</span>
                </div>
              </div>

              <p className="text-2xs text-[#444444] text-center mt-4.5">
                Investing <span className="font-semibold">₹{payable}</span> as Lumpsum in this fund will give you
                Returns of:
              </p>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="border border-[#D9D9D9] rounded-xl py-5 xl:px-4 text-sm leading-4 text-center space-y-3">
                  <p className="font-bold text-primaryBlue">{formatIndianNumber(tenYearSipValue, { prefix: "₹" })}</p>
                  <p className="text-[#444444]">
                    After <b>10</b> Years
                  </p>
                  <p className="text-[#444444] font-semibold">(+{tenYearGrowth.toFixed(0)}% Growth)</p>
                </div>

                <div className="border border-[#D9D9D9] rounded-xl py-5 xl:px-4 text-sm leading-4 text-center space-y-3">
                  <p className="font-bold text-primaryBlue">
                    {formatIndianNumber(fifteenYearSipValue, { prefix: "₹" })}
                  </p>
                  <p className="text-[#444444]">
                    After <b>15</b> Years
                  </p>
                  <p className="text-[#444444] font-semibold">(+{fifteenYearGrowth.toFixed(0)}% Growth)</p>
                </div>
              </div>

              <button
                onClick={handleInvesting}
                className="mt-4 lg:mt-auto w-full text-sm font-semibold border-2 border-[#FFFFFF82] bg-primaryGreen text-white py-2 rounded-full shine-button-hover"
              >
                Invest ₹{payable} Now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingSatate() {
  return (
    <div className="flex flex-col justify-between gap-3 w-full">
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 rounded-full shrink-0 bg-gray-200" />
        <div className="h-10 w-full max-w-[60%] bg-gray-200 rounded-md" />
        <div className="h-6 w-20 bg-gray-200 rounded-md ml-auto" />
      </div>

      <div className="h-4 w-1/2 mx-auto bg-gray-200 rounded-md" />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 h-32 bg-gray-200 rounded-xl" />
        <div className="flex-1 h-32 bg-gray-200 rounded-xl" />
      </div>

      <div className="h-10 w-full bg-gray-200 rounded-full mt-2" />
    </div>
  );
}
